class TabBehavior {
    /**
     * @param {HTMLElement} wrapper the wrapper element containing the elements to be converted to the tabs component
     * @param {object} config - The configuration object.
     * @param {string} config.appendSelector - the element where to append the new tablist
     * @param {string} config.buttonSelector - the pane controllers selector
     * @param {boolean} config.closeIfActive - whether the active button can close itself
     * @param {boolean} config.enableArrowKeysTabs - enable keyboard arrows accessibility
     * @param {string} config.paneHeader - specify this if you want to use a header inside the pane to be shown from which to create the tab button control
     * @param {string} config.paneSelector - the selector that retrieves the various tab panes
     * @param {boolean} config.triggerFirst - whether to show the first tab open or not
     */
    constructor(wrapper, config) {
        if (!wrapper) return;

        this.config = config;

        this.buttonSelector = config.buttonSelector;
        this.closeIfActive = config.closeIfActive;
        this.enableArrowKeysTabs = config.enableArrowKeysTabs;
        this.paneHeader = config.paneHeader;
        this.paneSelector = config.paneSelector;
        this.triggerFirst = config.triggerFirst;

        this.tablistNode = null;
        this.tabs = wrapper.querySelectorAll(this.buttonSelector);
        this.tabpanels = wrapper.querySelectorAll(this.paneSelector);
        this.firstTab = null;
        this.lastTab = null;

        this.wrapper = wrapper;
        this.append = wrapper?.querySelector(config.appendSelector);
        this.initialize();
    }

    initialize() {
        // if there are no buttons to control the tabs (this.tabs=[])
        // we have to create them else we just have to hydrate them
        if (this.tabs.length === 0) {
            this._createTabList();
        } else if (this.buttonSelector) {
            this._hydrateTabList();
        }
        this._hydrateTabPanels();
        this.triggerFirst && this.setSelectedTab(this.firstTab);
        const id = this.wrapper.id || this.config.id || 'default';
        this.wrapper.setAttribute('data-core-tabs', id);
    }

    _createTabList() {
        if (this.tabpanels.length > 1 && this.paneHeader) {
            const tabList = document.createElement('div');
            tabList.setAttribute('role', 'tablist');

            [...this.tabpanels].forEach(pane => {
                const header = pane.querySelector(this.paneHeader);
                const button = document.createElement('button');
                button.setAttribute('aria-controls', pane.id);
                button.setAttribute('role', 'tab');
                button.innerHTML = header.textContent || header.innerText;
                button.id = pane.id + '-label';
                tabList.appendChild(button);
            });
            if (this.append) {
                this.wrapper.insertBefore(tabList, this.append.nextSibling);
            } else {
                this.wrapper.prepend(tabList);
            }

            this.tablistNode = tabList;
            this.tabs = Array.from(this.tablistNode.querySelectorAll('[role=tab]'));
        }
    }
    _hydrateTabList() {
        this.tablistNode = this.tabs[0].parentNode;
        this.tablistNode.setAttribute('role', 'tablist');
        this.tabs = Array.from(this.tablistNode.querySelectorAll('[role=tab]'));
    }
    _hydrateTabButton(tab) {
        tab.tabIndex = -1;
        tab.setAttribute('aria-selected', 'false');
        tab.addEventListener('keydown', this.onKeydown.bind(this));
        tab.addEventListener('click', this.onClick.bind(this));
    }
    _hydrateTabPane(pane) {
        pane.tabIndex = -1;
        pane.setAttribute('role', 'tabpanel');
        const childNodes = [...pane.childNodes];
        if (this.paneHeader) {
            pane.setAttribute('aria-labelledby', pane.id + '-label');
        }
        return pane.notAvailable || childNodes.length === 0;
    }
    _hydrateTabPanels() {
        const populatePanels = this.tabpanels.length === 0;

        for (let i = 0; i < this.tabs.length; i += 1) {
            const tab = this.tabs[i];
            this._hydrateTabButton(tab);
            if (!this.firstTab) {
                this.firstTab = tab;
            }
            this.lastTab = tab;

            const target = tab.getAttribute('aria-controls');
            const tabpanel = this.wrapper.querySelector(target) || this.wrapper.querySelector('#' + target);
            const disable = this._hydrateTabPane(tabpanel);
            if (disable) {
                tab.classList.add('inactive');
                tab.setAttribute('aria-disabled', 'true');
                if (this.firstTab === tab) this.firstTab = undefined;
            }
            populatePanels && this.tabpanels.push(tabpanel);
        }
    }

    setSelectedTab(currentTab, fallbackEventKey) {
        const disable = (tab, i) => {
            tab.setAttribute('aria-selected', 'false');
            tab.classList.remove('active');
            tab.tabIndex = -1;
            this.tabpanels[i].tabIndex = -1;
        };
        const enable = (tab, i) => {
            tab.classList.add('active');
            tab.setAttribute('aria-selected', 'true');
            tab.removeAttribute('tabindex');
            this.tabpanels[i].tabIndex = 0;
        };

        for (let i = 0; i < this.tabs.length; i += 1) {
            const tab = this.tabs[i];
            if (currentTab === tab) {
                if (currentTab.classList.contains('inactive')) {
                    this.eventKeys(currentTab, fallbackEventKey);
                } else if (currentTab.classList.contains('active') && this.closeIfActive) {
                    disable(tab, i);
                } else {
                    enable(tab, i);
                    if (
                        this.tabs.length === 2 &&
                        i === this.tabs.length - 1 &&
                        this.tabs[0].classList.contains('inactive')
                    ) {
                        this.enableLastOne = true;
                    }
                }
            } else {
                if (this.enableLastOne) {
                    this.enableLastOne = false;
                    return;
                }
                disable(tab, i);
            }
        }
    }
    moveFocusToTab(currentTab, fallbackEventKey) {
        currentTab.focus();
        this.setSelectedTab(currentTab, fallbackEventKey);
    }
    moveFocusToPreviousTab(currentTab, fallbackEventKey) {
        let index;

        if (currentTab === this.firstTab) {
            this.moveFocusToTab(this.lastTab, fallbackEventKey);
        } else {
            index = this.tabs.indexOf(currentTab);
            this.moveFocusToTab(this.tabs[index - 1], fallbackEventKey);
        }
    }
    moveFocusToNextTab(currentTab, fallbackEventKey) {
        let index;

        if (currentTab === this.lastTab) {
            this.moveFocusToTab(this.firstTab, fallbackEventKey);
        } else {
            index = this.tabs.indexOf(currentTab);
            this.moveFocusToTab(this.tabs[index + 1], fallbackEventKey);
        }
    }
    eventKeys(tgt, eventKey) {
        let flag = false;
        switch (eventKey) {
            case 'ArrowLeft':
                this.moveFocusToPreviousTab(tgt, 'ArrowLeft');
                flag = true;
                break;

            case 'ArrowRight':
                this.moveFocusToNextTab(tgt, 'ArrowRight');
                flag = true;
                break;

            case 'Home':
                this.moveFocusToTab(this.firstTab, 'ArrowRight');
                flag = true;
                break;

            case 'End':
                this.moveFocusToTab(this.lastTab, 'ArrowLeft');
                flag = true;
                break;

            default:
                break;
        }
        return flag;
    }
    onKeydown(event) {
        if (this.eventKeys(event.currentTarget, event.key)) {
            event.stopPropagation();
            event.preventDefault();
        }
    }
    onClick(event) {
        this.setSelectedTab(event.currentTarget);
    }
}

module.exports = TabBehavior;
